import * as HLF from "html-living-framework";
import * as springroll from "springroll";
import * as Constants from "./Data/Constants";
import { CaptionManager } from "./GameObjects/CaptionManager";
import { LunaCaptionRenderer } from "./GameObjects/LunaCaptionRenderer";
import { GameDoll } from "./States/GameDoll";
import { GameDress } from "./States/GameDress";
import { GameHieroglyph } from "./States/GameHieroglyph";
import { GameMask } from "./States/GameMask";
import { GameQuilt } from "./States/GameQuilt";
import { Interstitial } from "./States/Interstitial";
import { Menu } from "./States/Menu";
import { SplashScreen } from "./States/SplashScreen";

import { GoogleAnalytics } from "springroll-google-analytics-plugin";

import * as FrameworkConfig from "../json/app-config.json";
import * as CaptionData from "../json/close-caption.json";

export class Application extends HLF.Application {
    private static _instance: Application;
    private _springroll!: springroll.Application;
    private _captions!: CaptionManager;

    private constructor() {
        super(FrameworkConfig.config);
    }

    static get instance() {
        if (Application._instance === undefined) {
            Application._instance = new Application();
        }
        return Application._instance;
    }

    public get analytics() {
        return this._springroll.getPlugin("GoogleAnalytics") as (GoogleAnalytics & springroll.ApplicationPlugin);
    }

    public init(): void {
        super.init();

        this.initSpringroll();

        this._resizer.sizeMax = new PIXI.Point(1663, 768);

        this._captions = new CaptionManager(CaptionData.default);
        this.stage.addChild(this._captions);

        // If we detect we are in the pbs kids games app, disable focus based muting
        if (/packageName=org\.pbskids\.gamesapp/.test(window.location.search)) {
            this._audio.setFocusMutingActive(false);
        }

        this.onResize();
    }

    public update(): void {
        super.update();
        const deltaTime: number = PIXI.ticker.shared.elapsedMS / 1000;
        this._captions.update(deltaTime);
    }

    protected registerLoadingScreens(): void {
        this._loader.registerLoadScreen(
            Constants.LOAD_SPLASH,
            new SplashScreen(),
            SplashScreen.getRequiredAssets,
        );
        this._loader.registerLoadScreen(
            Constants.LOAD_INTERSTITIAL,
            new Interstitial(),
            Interstitial.getRequiredAssets,
        );
    }

    protected registerPopups(): void {
        // override
    }

    protected registerStates(): void {
        this._state.registerState(
            Constants.STATE_MENU,
            () => new Menu(),
            Menu.getRequiredAssets,
        );
        this._state.registerState(
            Constants.STATE_DOLL,
            () => new GameDoll(),
            GameDoll.getRequiredAssets,
        );
        this._state.registerState(
            Constants.STATE_DRESS,
            () => new GameDress(),
            GameDress.getRequiredAssets,
        );
        this._state.registerState(
            Constants.STATE_HIEROGLYPH,
            () => new GameHieroglyph(),
            GameHieroglyph.getRequiredAssets,
        );
        this._state.registerState(
            Constants.STATE_MASK,
            () => new GameMask(),
            GameMask.getRequiredAssets,
        );
        this._state.registerState(
            Constants.STATE_QUILT,
            () => new GameQuilt(),
            GameQuilt.getRequiredAssets,
        );
    }

    protected onLoadRequiredAssetsComplete(): void {
        super.onLoadRequiredAssetsComplete();

        this._audio.setCategoryVolume(HLF.AudioCategory.SFX.toString(), 0.3);
        this._audio.setCategoryVolume(HLF.AudioCategory.BGM.toString(), 0.3);

        this._captions.setRenderer(new LunaCaptionRenderer(Constants.FONT_GEL_MED_35, 70));
        this._captions.onResize(this._size);

        if (Constants.DEBUG_DEFAULT_STATE === undefined) {
            if (Constants.DEBUG_SKIP_ONBOARDING === true) {
                this._state.transitionTo(Constants.STATE_MENU, Constants.LOAD_SPLASH);
            }
            else {
                this._state.transitionTo(Constants.STATE_MASK, Constants.LOAD_SPLASH);
            }
        }
        else {
            this._state.transitionTo(Constants.DEBUG_DEFAULT_STATE, Constants.LOAD_SPLASH);
        }
    }

    private initSpringroll(): void {
        springroll.Application.uses(new GoogleAnalytics());
        this._springroll = new springroll.Application({
            features: {
                captions: true,
                sound: true,
                vo: false,
                music: false,
                sfx: false,
            },
        });

        this._springroll.state.ready.subscribe(() => {
            this.onSpringrollInitialized();
        });

        this._springroll.state.pause.subscribe((isPaused: boolean) => {
            this.onPause(isPaused);
        });

        this._springroll.state.playOptions.subscribe((playOptions: object) => {
            console.log("New playOptions value set to", playOptions);
        });

        this._springroll.state.captionsMuted.subscribe((result: boolean) => {
            this.onCaptionsMuted(result);
        });

        this._springroll.state.soundVolume.subscribe((result: number) => {
            this.onSoundVolume(result);
        });
    }

    private onSpringrollInitialized(): void {
        // override
    }

    // Springroll message listeners
    private onCaptionsMuted(pMuted: boolean): void {
        this._captions.active = !pMuted;
    }

    private onSoundVolume(pResults: number): void {
        this._audio.masterVolume = pResults;
    }

    private onPause(pIsPaused: boolean): void {
        console.log("paused: " + pIsPaused);
    }
}
